import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { TrendUp, TrendDown, Users, Package, CurrencyDollar, ArrowUpRight, Warning } from "@phosphor-icons/react";
import OnboardingChecklist from "../components/OnboardingChecklist";
import { useAuth } from "../lib/auth";
import { ReportChart } from "../components/ReportChart";
import { fmtNumber } from "../lib/i18n";
import { DotsSixVertical, ArrowsOutLineHorizontal, ArrowsInLineHorizontal } from "@phosphor-icons/react";

function KpiTile({ label, value, sub, accent, testId }) {
  return (
    <div className="border-r border-b border-zinc-200 p-6 hover:bg-zinc-50 transition-colors" data-testid={testId}>
      <div className="label-sm text-zinc-500 flex items-center justify-between">
        <span>{label}</span>
        {accent && <span className="w-1.5 h-1.5 bg-[#FF3E00]" />}
      </div>
      <div className="font-display font-black text-4xl tnum mt-3 tracking-tight">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-2 flex items-center gap-1.5">{sub}</div>}
    </div>
  );
}

function fmt(n, prefix = "$") {
  return `${prefix}${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "superuser";
  const [fin, setFin] = useState(null);
  const [hr, setHr] = useState(null);
  const [scm, setScm] = useState(null);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/finance/summary").then((r) => setFin(r.data)),
      api.get("/hr/summary").then((r) => setHr(r.data)),
      api.get("/scm/summary").then((r) => setScm(r.data)),
      api.get("/audit-logs?limit=8").then((r) => setAudit(r.data)),
    ]).catch(() => {});
  }, []);

  return (
    <div className="p-6 sm:p-8" data-testid="dashboard-page">
      {/* Item 6: onboarding checklist is admin-only */}
      {isAdmin && <OnboardingChecklist />}
      {/* Header */}
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          {/* Item 7: the "CONTROL_ROOM" label is admin/owner language */}
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">{isAdmin ? "CONTROL_ROOM" : "DASHBOARD"} / 2026.02</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">
            Operations overview.
          </h1>
          <p className="text-sm text-zinc-500 mt-2">Real-time snapshot across Finance, HR, and Supply Chain.</p>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 border-t border-l border-zinc-200">
        <KpiTile
          testId="kpi-ar-open"
          label="AR Outstanding"
          value={fmt(fin?.ar_open)}
          sub={<><CurrencyDollar size={12} /> {fin ? `${fmt(fin.ar_total)} total` : "—"}</>}
          accent
        />
        <KpiTile
          testId="kpi-ap-open"
          label="AP Outstanding"
          value={fmt(fin?.ap_open)}
          sub={<><CurrencyDollar size={12} /> {fin ? `${fmt(fin.ap_total)} total` : "—"}</>}
        />
        <KpiTile
          testId="kpi-employees"
          label="Active Employees"
          value={hr?.active_employees ?? "—"}
          sub={<><Users size={12} /> {hr ? `${fmt(hr.monthly_payroll)} payroll/mo` : "—"}</>}
        />
        <KpiTile
          testId="kpi-inventory"
          label="Inventory Value"
          value={fmt(scm?.inventory_value)}
          sub={<><Package size={12} /> {scm ? `${scm.product_count} SKUs` : "—"}</>}
        />
        <KpiTile
          testId="kpi-net"
          label="Net AR–AP"
          value={fmt(fin?.net_position)}
          sub={fin && fin.net_position >= 0 ? <><TrendUp size={12} className="text-emerald-600" />Positive</> : <><TrendDown size={12} className="text-red-600" />Watch</>}
        />
        <KpiTile
          testId="kpi-low-stock"
          label="Low Stock SKUs"
          value={scm?.low_stock_count ?? "—"}
          sub={scm?.low_stock_count > 0 ? <><Warning size={12} className="text-amber-600" />Reorder needed</> : <>All healthy</>}
        />
        <KpiTile
          testId="kpi-pending-leaves"
          label="Pending Leaves"
          value={hr?.pending_leaves ?? "—"}
          sub="Awaiting approval"
        />
        <KpiTile
          testId="kpi-revenue"
          label="Fulfilled Revenue"
          value={fmt(scm?.fulfilled_revenue)}
          sub="From SOs"
        />
      </div>

      {/* Lower grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-zinc-200 mt-8 border border-zinc-200">
        {/* Module quick access */}
        <div className="bg-white p-6 lg:col-span-3">
          <div className="label-sm text-zinc-500 mb-4">Modules</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-zinc-200">
            {[
              { to: "/finance", code: "01", label: "Finance", desc: "GL · AR · AP · Journals", n: fin?.account_count, suffix: "accounts" },
              { to: "/hr", code: "02", label: "Human Resources", desc: "People · Payroll · Leaves", n: hr?.total_employees, suffix: "employees" },
              { to: "/scm", code: "03", label: "Supply Chain", desc: "Inventory · POs · SOs", n: scm?.product_count, suffix: "SKUs" },
            ].map((m) => (
              <Link
                key={m.to}
                to={m.to}
                data-testid={`module-card-${m.to.slice(1)}`}
                className="bg-white p-5 border border-transparent hover:border-zinc-950 hover:bg-zinc-50 transition-all group"
              >
                <div className="flex items-start justify-between">
                  <span className="font-mono text-[10px] text-zinc-400">{m.code}</span>
                  <ArrowUpRight size={14} className="text-zinc-400 group-hover:text-[#FF3E00] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
                <div className="font-display font-bold text-lg mt-6">{m.label}</div>
                <div className="text-xs text-zinc-500 mt-1">{m.desc}</div>
                <div className="tnum text-sm text-zinc-950 mt-3">
                  {m.n ?? "—"} <span className="text-zinc-400">{m.suffix}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Pinned reports */}
        <PinnedReports />

        {/* Activity log */}
        <div className="bg-white p-6">
          <div className="label-sm text-zinc-500 mb-4">Recent Activity</div>
          <div className="space-y-3" data-testid="activity-feed">
            {audit.length === 0 && <div className="text-xs text-zinc-400 font-mono">NO ACTIVITY YET</div>}
            {audit.map((a) => (
              <div key={a.id} className="flex items-start gap-2 text-xs">
                <span className="font-mono text-[10px] text-[#FF3E00] mt-0.5">▸</span>
                <div className="flex-1 min-w-0">
                  <div className="text-zinc-950">
                    <span className="font-semibold">{a.action}</span>{" "}
                    <span className="text-zinc-500">on</span>{" "}
                    <span className="font-mono">{a.resource}</span>
                  </div>
                  <div className="text-[10px] text-zinc-400 font-mono">
                    {a.user_email} · {new Date(a.timestamp).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const MODULE_ROUTE = { finance: "/finance", hr: "/hr", scm: "/scm", oman: "/oman", banking: "/banking", aml: "/aml", ifrs: "/ifrs", hse: "/hse", eam: "/eam" };

const fmtTotal = (v) => (typeof v === "number" ? fmtNumber(v, { maximumFractionDigits: 2 }) : String(v));
const PERIODS = [["ytd", "YTD"], ["30d", "30D"], ["90d", "90D"], ["all", "ALL"]];
const isoDaysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
export const periodRange = (p) => {
  const to = new Date().toISOString().slice(0, 10);
  if (p === "ytd") return { from: `${new Date().getFullYear()}-01-01`, to };
  if (p === "30d") return { from: isoDaysAgo(30), to };
  if (p === "90d") return { from: isoDaysAgo(90), to };
  return {};
};

function PinnedReports() {
  const [reports, setReports] = useState([]);
  const [data, setData] = useState({});
  const [periods, setPeriods] = useState({});
  const [sizes, setSizes] = useState({});
  const [dragId, setDragId] = useState(null);

  const toggleSize = (e, rep) => {
    e.preventDefault(); e.stopPropagation();
    const next = (sizes[rep.id] || "single") === "double" ? "single" : "double";
    setSizes((s) => ({ ...s, [rep.id]: next }));
    api.patch(`/reports/favourites/${rep.id}/size`, { size: next }).catch(() => {});
  };
  const [overId, setOverId] = useState(null);

  const onDrop = (e, targetId) => {
    e.preventDefault();
    if (!dragId || dragId === targetId) { setDragId(null); setOverId(null); return; }
    const ids = reports.map((r) => r.id);
    const from = ids.indexOf(dragId), to = ids.indexOf(targetId);
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    setReports((rs) => ids.map((id) => rs.find((r) => r.id === id)));
    setDragId(null); setOverId(null);
    api.put("/reports/favourites/order", { report_ids: ids }).catch(() => {});
  };

  const load = (rep, period) => api.get(`/reports/${rep.id}`, { params: periodRange(period) }).then((d) => setData((s) => ({ ...s, [rep.id]: d.data }))).catch(() => {});

  useEffect(() => {
    api.get("/reports/favourites").then((r) => {
      setReports(r.data.reports);
      setPeriods(r.data.periods || {});
      setSizes(r.data.sizes || {});
      r.data.reports.forEach((rep) => load(rep, rep.period || "all"));
    }).catch(() => {});
  }, []);

  const setPeriod = async (e, rep, period) => {
    e.preventDefault(); e.stopPropagation();
    setPeriods((s) => ({ ...s, [rep.id]: period }));
    setData((s) => ({ ...s, [rep.id]: undefined }));
    load(rep, period);
    api.patch(`/reports/favourites/${rep.id}/period`, { period }).catch(() => {});
  };
  return (
    <div className="bg-white p-6 lg:col-span-2" data-testid="pinned-reports-card">
      <div className="flex items-center justify-between mb-4">
        <div className="label-sm text-zinc-500">Pinned Reports</div>
        <span className="text-[10px] font-mono text-zinc-400">{reports.length} pinned{reports.length > 1 ? " · drag to reorder" : ""}</span>
      </div>
      {reports.length === 0 && (
        <div className="text-xs text-zinc-400 font-mono" data-testid="pinned-reports-empty">
          NO PINNED REPORTS — open any module's Reports tab and click the pin icon next to a report.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="pinned-reports-list">
        {reports.map((r) => {
          const d = data[r.id];
          const totals = d ? Object.entries(d.totals || {}).slice(0, 3) : [];
          return (
            <Link key={r.id} to={`${MODULE_ROUTE[r.module] || "/"}?tab=Reports&report=${r.id}&period=${periods[r.id] || "all"}`} data-testid={`pinned-report-${r.id}`}
              draggable onDragStart={(e) => { setDragId(r.id); e.dataTransfer.effectAllowed = "move"; }} onDragEnd={() => { setDragId(null); setOverId(null); }}
              onDragOver={(e) => { e.preventDefault(); if (overId !== r.id) setOverId(r.id); }} onDrop={(e) => onDrop(e, r.id)}
              onClick={(e) => { if (dragId) e.preventDefault(); }}
              className={`bg-white p-4 border transition-all group min-w-0 ${(sizes[r.id] || "single") === "double" ? "sm:col-span-2" : ""} ${dragId === r.id ? "opacity-40 border-dashed border-zinc-400" : overId === r.id && dragId ? "border-[#FF3E00] bg-orange-50/40" : "border-zinc-200 hover:border-zinc-950 hover:bg-zinc-50"}`}>
              <div className="flex items-start justify-between">
                <span className="font-mono text-[10px] uppercase text-zinc-400 flex items-center gap-1">
                  <DotsSixVertical size={12} className="text-zinc-300 group-hover:text-zinc-950 cursor-grab active:cursor-grabbing" data-testid={`pinned-drag-${r.id}`} />
                  {r.module}
                </span>
                <span className="flex items-center gap-2">
                  <button type="button" onClick={(e) => toggleSize(e, r)} title={(sizes[r.id] || "single") === "double" ? "Shrink to single width" : "Expand to double width"} data-testid={`pinned-size-${r.id}`}
                    className="text-zinc-300 hover:text-zinc-950 transition-colors">
                    {(sizes[r.id] || "single") === "double" ? <ArrowsInLineHorizontal size={12} /> : <ArrowsOutLineHorizontal size={12} />}
                  </button>
                  <ArrowUpRight size={12} className="text-zinc-400 group-hover:text-[#FF3E00] transition-all" />
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 mt-1">
                <div className="font-semibold text-sm truncate">{r.name}</div>
                {r.dated && (
                  <div className="flex shrink-0 border border-zinc-200" data-testid={`pinned-period-${r.id}`} role="group">
                    {PERIODS.map(([p, label]) => (
                      <button key={p} type="button" onClick={(e) => setPeriod(e, r, p)} data-testid={`pinned-period-${r.id}-${p}`}
                        className={`px-1.5 py-0.5 text-[9px] font-mono transition-colors ${(periods[r.id] || "all") === p ? "bg-zinc-950 text-white" : "text-zinc-500 hover:bg-zinc-100"}`}>{label}</button>
                    ))}
                  </div>
                )}
              </div>
              {!d && <div className="h-36 mt-2 bg-zinc-50 animate-pulse" data-testid={`pinned-chart-loading-${r.id}`} />}
              {d && d.chart && d.rows.length > 0 && (
                <div className="mt-2" data-testid={`pinned-chart-${r.id}`}>
                  <ReportChart chart={d.chart} columns={d.columns} rows={d.rows} compact={(sizes[r.id] || "single") !== "double"} wide={(sizes[r.id] || "single") === "double"} />
                </div>
              )}
              {d && (!d.chart || d.rows.length === 0) && (
                <div className="mt-3 grid grid-cols-3 gap-px bg-zinc-200" data-testid={`pinned-kpis-${r.id}`}>
                  <div className="bg-white py-2 pr-2"><div className="label-sm text-zinc-500">Rows</div><div className="font-display font-black text-lg tnum">{d.row_count}</div></div>
                  {totals.map(([k, v]) => (
                    <div key={k} className="bg-white py-2 px-2 min-w-0"><div className="label-sm text-zinc-500 truncate">{(d.columns.find((c) => c.key === k)?.label || k).replace(/_/g, " ")}</div><div className="font-display font-black text-lg tnum truncate">{fmtTotal(v)}</div></div>
                  ))}
                </div>
              )}
              {d && <div className="text-[10px] font-mono text-zinc-400 mt-2">{d.row_count} rows · {r.dated ? `${d.from || "start"} → ${d.to || "today"}` : "point in time"} · {d.generated_at.slice(11, 16)} UTC</div>}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
